import { useState, useEffect, useCallback } from 'react';
import { read } from 'xlsx';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Eye, Info, FileSpreadsheet, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface ImportedPosition {
  ticker: string;
  stockName: string;
  quantity: number;
  currentPrice: number;
  totalValue: number;
  localCurrency: string;
  gainPct: number;
  initialInvestment: number;
  buyPrice: number;
  brPerformance?: number;
  // IBKR-specific
  dividends?: any[];
  costBasis?: number;
}

interface PreviewRow {
  row: number;
  name: string;
  quantity: number;
  value: number;
  currency: string;
}

interface Props {
  onImportComplete: (positions: ImportedPosition[], source: 'excel' | 'ibkr') => void;
  buttonLabel?: string;
}

async function fetchRates(): Promise<Record<string, number>> {
  try {
    const res = await fetch('https://api.frankfurter.app/latest?from=CHF&to=USD,EUR,GBP,SEK,NOK,DKK,JPY,CAD,AUD,HKD,SGD');
    const data = await res.json();
    const rates: Record<string, number> = { CHF: 1 };
    for (const [cur, val] of Object.entries(data.rates)) {
      rates[cur] = 1 / (val as number);
    }
    return rates;
  } catch {
    return { CHF: 1, USD: 0.88, EUR: 0.95, GBP: 1.12, SEK: 0.083, NOK: 0.082, DKK: 0.127, JPY: 0.0059, CAD: 0.65, AUD: 0.57, HKD: 0.113, SGD: 0.66 };
  }
}

function detectCurrency(value: string): string {
  const v = (value || '').toUpperCase().trim();
  if (v.includes('USD') || v.startsWith('$')) return 'USD';
  if (v.includes('EUR') || v.startsWith('€')) return 'EUR';
  if (v.includes('GBP') || v.startsWith('£')) return 'GBP';
  if (v.includes('SEK')) return 'SEK';
  if (v.includes('NOK')) return 'NOK';
  if (v.includes('DKK')) return 'DKK';
  if (v.includes('JPY') || v.startsWith('¥')) return 'JPY';
  if (v.includes('CAD')) return 'CAD';
  if (v.includes('AUD')) return 'AUD';
  if (v.includes('HKD')) return 'HKD';
  if (v.includes('SGD')) return 'SGD';
  if (v.includes('CHF')) return 'CHF';
  return 'CHF';
}

function parseNum(v: any): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace(/[^0-9.\-]/g, '');
  return parseFloat(s) || 0;
}

export function PortfolioFileImport({ onImportComplete, buttonLabel = 'Import & Analyse' }: Props) {
  const [tab, setTab] = useState<'excel' | 'ibkr'>('excel');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [startLine, setStartLine] = useState(15);
  const [endLine, setEndLine] = useState(157);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<PreviewRow[] | null>(null);
  const [previewSheet, setPreviewSheet] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRates, setPreviewRates] = useState<Record<string, number> | null>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStep, setImportStep] = useState('');
  const { toast } = useToast();

  const acceptFile = (file: File) => {
    const isExcel = /\.(xlsx|xls|xlsm)$/i.test(file.name);
    const isCsv = /\.csv$/i.test(file.name);
    if (tab === 'excel' && isExcel) {
      setSelectedFile(file);
      setPreview(null);
    } else if (tab === 'ibkr' && (isCsv || isExcel)) {
      setSelectedFile(file);
      setPreview(null);
    } else {
      toast({ title: 'Invalid file', description: tab === 'excel' ? 'Use .xlsx/.xls/.xlsm' : 'Use .csv or .xlsx', variant: 'destructive' });
    }
  };

  const generatePreview = useCallback(async () => {
    if (!selectedFile || tab !== 'excel') return;
    setPreviewLoading(true);
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = read(data);
      const sheetName = workbook.SheetNames.find(n => n.toUpperCase().includes('PRVFIN'));
      if (!sheetName) {
        toast({ title: 'Sheet not found', description: `"PRVFIN" not found. Available: ${workbook.SheetNames.join(', ')}`, variant: 'destructive' });
        setPreviewLoading(false);
        return;
      }
      setPreviewSheet(sheetName);
      const ws = workbook.Sheets[sheetName];
      const cell = (addr: string) => ws?.[addr]?.v;

      const rows: PreviewRow[] = [];
      for (let r = startLine; r <= endLine; r++) {
        const name = String(cell(`C${r}`) || '').trim();
        if (!name) continue;
        const quantity = parseNum(cell(`G${r}`));
        if (quantity <= 0) continue;
        const value = parseNum(cell(`J${r}`));
        if (value <= 0) continue;
        let currency = 'CHF';
        const kVal = String(cell(`K${r}`) || '');
        const iVal = String(cell(`I${r}`) || '');
        const hVal = String(cell(`H${r}`) || '');
        if (kVal) currency = detectCurrency(kVal);
        else if (iVal) currency = detectCurrency(iVal);
        else if (hVal) currency = detectCurrency(hVal);
        rows.push({ row: r, name, quantity, value, currency });
      }
      setPreview(rows);
      if (rows.length > 0) setPreviewRates(await fetchRates());
      if (rows.length === 0) toast({ title: 'No data', description: `No positions in rows ${startLine}-${endLine}`, variant: 'destructive' });
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedFile, startLine, endLine, tab, toast]);

  useEffect(() => {
    if (selectedFile && tab === 'excel') generatePreview();
  }, [selectedFile, startLine, endLine, generatePreview, tab]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) acceptFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  };

  const importExcel = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setImportProgress(0);
    setImportStep('Reading Excel file...');
    try {
      setImportProgress(10);
      const data = await selectedFile.arrayBuffer();
      const workbook = read(data);
      const sheetName = workbook.SheetNames.find(n => n.toUpperCase().includes('PRVFIN'));
      if (!sheetName) throw new Error(`Sheet "PRVFIN" not found.`);
      const ws = workbook.Sheets[sheetName];
      const cell = (addr: string) => ws?.[addr]?.v;

      setImportStep('Fetching exchange rates...');
      setImportProgress(30);
      const rates = await fetchRates();

      setImportStep('Parsing positions...');
      setImportProgress(40);
      const positions: ImportedPosition[] = [];
      const seen = new Set<string>();

      for (let r = startLine; r <= endLine; r++) {
        const stockName = String(cell(`C${r}`) || '').trim();
        if (!stockName) continue;
        const quantity = parseNum(cell(`G${r}`));
        if (quantity <= 0) continue;
        const totalValueLocal = parseNum(cell(`J${r}`));
        if (totalValueLocal <= 0) continue;

        let localCurrency = 'CHF';
        const kVal = String(cell(`K${r}`) || '');
        const iVal = String(cell(`I${r}`) || '');
        const hVal = String(cell(`H${r}`) || '');
        if (kVal) localCurrency = detectCurrency(kVal);
        else if (iVal) localCurrency = detectCurrency(iVal);
        else if (hVal) localCurrency = detectCurrency(hVal);

        const currentPrice = totalValueLocal / quantity;
        const ticker = stockName.replace(/\s+(SA|AG|PLC|INC|CORP|LTD|NV|SE|SPA|AB|OYJ|ASA)\.?$/i, '').split(/\s+/).slice(0, 2).join(' ').toUpperCase();
        if (seen.has(ticker)) continue;
        seen.add(ticker);

        const buyPrice = parseNum(cell(`E${r}`)) || parseNum(cell(`F${r}`));
        const gainPct = buyPrice > 0 ? (currentPrice - buyPrice) / buyPrice : 0;
        const initialInvestment = buyPrice > 0 ? buyPrice * quantity : totalValueLocal;
        const rateToChf = rates[localCurrency] || 1;
        const brVal = parseNum(cell(`BR${r}`));

        positions.push({
          ticker, stockName, quantity, currentPrice, totalValue: totalValueLocal * rateToChf,
          localCurrency, gainPct, initialInvestment: initialInvestment * rateToChf, buyPrice: buyPrice || currentPrice,
          brPerformance: brVal,
        });
      }

      if (positions.length === 0) throw new Error('No valid positions found.');
      setImportProgress(100);
      setImportStep(`${positions.length} positions imported`);
      toast({ title: 'Import Complete', description: `${positions.length} positions loaded from Excel.` });
      onImportComplete(positions, 'excel');
    } catch (err: any) {
      toast({ title: 'Import Failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const importIBKR = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setImportProgress(0);
    setImportStep('Reading IBKR file...');
    try {
      setImportProgress(10);
      const data = await selectedFile.arrayBuffer();
      const workbook = read(data, { type: 'array' });
      const ws = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[][] = [];
      const range = ws['!ref'];
      if (!range) throw new Error('Empty file');
      
      // Convert to JSON rows
      const jsonRows = read(data, { type: 'array' });
      const sheet = jsonRows.Sheets[jsonRows.SheetNames[0]];
      
      // Parse IBKR CSV-style: look for "Open Positions" or similar headers
      const allRows: string[][] = [];
      const ref = sheet['!ref'];
      if (ref) {
        const [, endCell] = ref.split(':');
        const maxRow = parseInt(endCell.replace(/[A-Z]/g, ''));
        const maxCol = endCell.replace(/[0-9]/g, '');
        
        for (let r = 1; r <= maxRow; r++) {
          const row: string[] = [];
          for (let c = 0; c < 20; c++) {
            const colLetter = String.fromCharCode(65 + c);
            const cellVal = sheet[`${colLetter}${r}`]?.v;
            row.push(cellVal != null ? String(cellVal) : '');
          }
          allRows.push(row);
        }
      }

      setImportStep('Parsing positions...');
      setImportProgress(40);

      const positions: ImportedPosition[] = [];
      const seen = new Set<string>();
      let inPositions = false;
      let headerRow: string[] = [];
      const rates = await fetchRates();

      for (const row of allRows) {
        const first = (row[0] || '').trim();
        const second = (row[1] || '').trim();
        
        // Detect positions section
        if (first === 'Open Positions' || first === 'Positions' || (first === 'Data' && second === 'Open Positions')) {
          inPositions = true;
          continue;
        }
        if (first === 'Header' && inPositions) {
          headerRow = row;
          continue;
        }
        if (inPositions && first === 'Data' && row.some(c => parseNum(c) > 0)) {
          const symbol = (row[2] || row[1] || '').trim();
          if (!symbol || seen.has(symbol)) continue;
          
          const quantity = Math.abs(parseNum(row[3] || row[4]));
          const costBasis = parseNum(row[4] || row[5]);
          const currentPrice = parseNum(row[5] || row[6]);
          const marketValue = parseNum(row[6] || row[7]);
          const currency = (row[1] || 'USD').trim();

          if (quantity <= 0 || marketValue <= 0) continue;
          seen.add(symbol);

          const rateToChf = rates[currency] || rates['USD'] || 0.88;
          const buyPrice = costBasis > 0 ? costBasis / quantity : currentPrice;
          const gainPct = buyPrice > 0 ? (currentPrice - buyPrice) / buyPrice : 0;

          positions.push({
            ticker: symbol, stockName: symbol, quantity, currentPrice,
            totalValue: marketValue * rateToChf, localCurrency: currency,
            gainPct, initialInvestment: (buyPrice * quantity) * rateToChf,
            buyPrice, costBasis,
          });
        }
        // Stop at next section
        if (inPositions && first !== 'Data' && first !== 'Header' && first !== '' && !first.startsWith('Total')) {
          if (positions.length > 0) break;
        }
      }

      // Fallback: try generic parsing if no structured positions found
      if (positions.length === 0) {
        for (const row of allRows) {
          const possibleTicker = (row[0] || '').trim();
          const possibleQty = parseNum(row[1] || row[2]);
          const possibleValue = parseNum(row[3] || row[4] || row[2]);
          if (possibleTicker && possibleQty > 0 && possibleValue > 0 && !seen.has(possibleTicker) && possibleTicker.length < 20) {
            seen.add(possibleTicker);
            const price = possibleValue / possibleQty;
            positions.push({
              ticker: possibleTicker, stockName: possibleTicker, quantity: possibleQty,
              currentPrice: price, totalValue: possibleValue, localCurrency: 'USD',
              gainPct: 0, initialInvestment: possibleValue, buyPrice: price,
            });
          }
        }
      }

      if (positions.length === 0) throw new Error('No positions found in IBKR file. Ensure it contains an "Open Positions" section.');
      setImportProgress(100);
      setImportStep(`${positions.length} positions imported from IBKR`);
      toast({ title: 'IBKR Import Complete', description: `${positions.length} positions loaded.` });
      onImportComplete(positions, 'ibkr');
    } catch (err: any) {
      toast({ title: 'Import Failed', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = () => {
    if (tab === 'excel') importExcel();
    else importIBKR();
  };

  return (
    <div className="space-y-4">
      <Tabs value={tab} onValueChange={(v) => { setTab(v as 'excel' | 'ibkr'); setSelectedFile(null); setPreview(null); }}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="excel" className="gap-2"><FileSpreadsheet className="h-4 w-4" />Excel (PRVFIN)</TabsTrigger>
          <TabsTrigger value="ibkr" className="gap-2"><FileText className="h-4 w-4" />IBKR CSV/Excel</TabsTrigger>
        </TabsList>

        <TabsContent value="excel" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Line</Label>
              <Input type="number" min="1" value={startLine} onChange={(e) => setStartLine(Number(e.target.value))} disabled={loading} />
            </div>
            <div className="space-y-2">
              <Label>End Line</Label>
              <Input type="number" min="1" value={endLine} onChange={(e) => setEndLine(Number(e.target.value))} disabled={loading} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ibkr" className="mt-4">
          <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
            <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">Upload your IBKR Activity Statement (.csv or .xlsx). The parser will locate the Open Positions section automatically.</p>
          </div>
        </TabsContent>
      </Tabs>

      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <label htmlFor="portfolio-file-upload" className="cursor-pointer">
          <span className="text-sm text-muted-foreground">
            {selectedFile ? selectedFile.name : `Drag & drop or click to select ${tab === 'excel' ? 'Excel (.xlsx)' : 'IBKR (.csv/.xlsx)'}`}
          </span>
          <input id="portfolio-file-upload" type="file" accept={tab === 'excel' ? '.xlsx,.xls,.xlsm' : '.csv,.xlsx,.xls'} className="hidden" onChange={handleFileSelect} disabled={loading} />
        </label>
      </div>

      {loading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">{importStep}</p>
            <span className="text-sm font-mono text-muted-foreground">{importProgress}%</span>
          </div>
          <Progress value={importProgress} className="h-2" />
        </div>
      )}

      {previewLoading && <div className="text-center text-sm text-muted-foreground py-4">Scanning file...</div>}

      {preview && preview.length > 0 && tab === 'excel' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{preview.length} positions in "{previewSheet}" (rows {startLine}–{endLine})</span>
          </div>
          <div className="max-h-48 overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14">Row</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead className="w-16">Ccy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.slice(0, 15).map((p) => (
                  <TableRow key={p.row}>
                    <TableCell className="text-muted-foreground text-xs">{p.row}</TableCell>
                    <TableCell className="text-xs font-medium truncate max-w-[200px]">{p.name}</TableCell>
                    <TableCell className="text-right text-xs">{p.quantity.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs">{p.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</TableCell>
                    <TableCell className="text-xs">{p.currency}</TableCell>
                  </TableRow>
                ))}
                {preview.length > 15 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-xs text-muted-foreground">...and {preview.length - 15} more</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Button onClick={handleImport} disabled={loading || !selectedFile} className="w-full">
        {loading ? 'Importing...' : buttonLabel}
      </Button>
    </div>
  );
}
